import React, { useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import { Box, CardActionArea, Chip, Stack, Switch } from "@mui/material";
import { Currency, ProductInterface, User } from "../types/common.types";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedProduct } from "../redux/slices/AppState";
import { RootState } from "../redux/store";
import { get } from "lockr";
import { useTranslation } from "../hooks/useTranslation";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import {
  formatPromotionDiscount,
  getProductPromotion,
} from "./promotions/promotionDisplay";
const DEFAULT_PRODUCT_IMAGE = require("../assets/no-product-image.png");

export type ProductProp = ProductInterface;

export enum ProductType {
  Drink = 1,
  Food,
}

export interface Props {
  product: ProductProp;
  productType: ProductType;
  parent: "Page" | "Modal";
}

const Product = (props: Props) => {
  const { product, parent } = props;
  const promotions = useSelector((state: RootState) => state.Promotion.promotions);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const productInStock = useTranslation("product_is_in_stock");
  const productOutOfStock = useTranslation("out_of_stock");

  const [imageSrc, setImageSrc] = useState(product?.productPhoto?.secureUrl);
  const productHasPhoto = Boolean(imageSrc);

  let defaultLanguage = "en";
  let currency: Currency = { name: "", prefix: "" };
  const userData = get("user") as User | undefined;

  if (userData) {
    defaultLanguage = userData.defaultLanguage;
    currency = userData.currency;
  }

  const productPrice = product.prices?.regular ?? 0;
  const productName =
    product.names?.[defaultLanguage] || product.names?.en || "Untitled product";
  const productDescription =
    product.descriptions?.[defaultLanguage] || product.descriptions?.en || "";
  const promotion = getProductPromotion(product, props.productType, promotions);

  const handleClick = () => {
    if (parent === "Modal") return;

    dispatch(setSelectedProduct(product));
    navigate(`${product.id}`);
  };

  const handleAvailabilityToggleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleAvailabilityToggle = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();

    // TODO: Wire this to the product availability update API once the backend
    // contract and Redux refresh strategy are confirmed.
    return;
  };

  return (
    <Card
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "8px",
        position: "relative",
        overflow: "hidden",
        background: "#16100f",
        border: `1px solid ${
          product.inStock
            ? "rgba(131, 60, 65, 0.18)"
            : "rgba(122, 101, 96, 0.18)"
        }`,
        opacity: product.inStock ? 1 : 0.62,
        boxShadow: "none",
        transition: "border-color 0.15s ease, background 0.15s ease",
        "&:hover": {
          borderColor: "rgba(158, 74, 80, 0.45)",
          background: "#1b1312",
        },
      }}
    >
      <CardActionArea
        component="div"
        onClick={handleClick}
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "stretch",
          p: 0,
          color: "inherit",
          cursor: parent === "Modal" ? "default" : "pointer",
          "&:hover .product-edit-icon": {
            opacity: 1,
          },
        }}
      >
        <CardContent
          sx={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: {
              xs: "72px minmax(0, 1fr)",
              sm: "82px minmax(0, 1fr)",
            },
            gap: 1.5,
            p: 1.25,
            "&:last-child": {
              pb: 1.25,
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: "1 / 1",
              borderRadius: "7px",
              overflow: "hidden",
              border: "1px solid rgba(131, 60, 65, 0.18)",
              bgcolor: "#0f0b0b",
            }}
          >
            <CardMedia
              component="img"
              image={productHasPhoto ? imageSrc : DEFAULT_PRODUCT_IMAGE}
              alt={productName}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                filter: product.inStock ? "none" : "grayscale(0.65)",
              }}
              onError={() => setImageSrc(DEFAULT_PRODUCT_IMAGE)}
            />
          </Box>

          <Stack spacing={1} sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    color: "#f0ece8",
                    fontSize: "13px",
                    fontWeight: 600,
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {productName}
                </Typography>
                <Typography
                  sx={{
                    color: "#7a6560",
                    fontSize: "11px",
                    lineHeight: 1.35,
                    mt: 0.35,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {productDescription || "No description"}
                </Typography>
              </Box>
              <Box
                className="product-edit-icon"
                sx={{
                  opacity: { xs: 1, md: 0.45 },
                  color: "#7a6560",
                  display: "flex",
                  transition: "opacity 0.15s ease, color 0.15s ease",
                  flexShrink: 0,
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 15 }} />
              </Box>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ minWidth: 0 }}
            >
              <Typography
                sx={{
                  color: "#f0ece8",
                  fontFamily:
                    '"Cormorant Infant", Georgia, "Times New Roman", serif',
                  fontSize: "20px",
                  fontWeight: 600,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                {`${currency.prefix}${productPrice}`}
              </Typography>
              <Typography sx={{ color: "#4b3733", fontSize: "11px" }}>
                ·
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.3}
                sx={{ color: "#d5a84c" }}
              >
                <PaidOutlinedIcon sx={{ fontSize: 13 }} />
                <Typography
                  sx={{
                    fontFamily:
                      '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                    fontSize: "10px",
                  }}
                >
                  0
                </Typography>
              </Stack>
              <Typography
                sx={{
                  color: "#7a6560",
                  fontSize: "11px",
                  textTransform: "capitalize",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {product.category?.name || "Menu item"}
              </Typography>
            </Stack>

            {promotion && (
              <Chip
                size="small"
                label={`${promotion.name} · ${formatPromotionDiscount(promotion)}`}
                sx={{
                  alignSelf: "flex-start",
                  maxWidth: "100%",
                  bgcolor: "rgba(213, 168, 76, 0.14)",
                  color: "#f6d58b",
                  border: "1px solid rgba(213, 168, 76, 0.28)",
                  borderRadius: "7px",
                  "& .MuiChip-label": {
                    px: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            )}

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 0.8,
                  py: 0.3,
                  borderRadius: "999px",
                  background: product.inStock
                    ? "rgba(74, 222, 128, 0.13)"
                    : "rgba(248, 113, 113, 0.12)",
                  color: product.inStock ? "#4ade80" : "#f87171",
                  border: `1px solid ${
                    product.inStock
                      ? "rgba(74, 222, 128, 0.26)"
                      : "rgba(248, 113, 113, 0.24)"
                  }`,
                }}
              >
                <Inventory2OutlinedIcon sx={{ fontSize: 12 }} />
                <Typography
                  sx={{
                    fontFamily:
                      '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                    fontSize: "9px",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  {product.inStock ? productInStock : productOutOfStock}
                </Typography>
              </Box>
              <Box onClick={handleAvailabilityToggleClick}>
                <Switch
                  checked={product.inStock}
                  onChange={handleAvailabilityToggle}
                  size="small"
                  inputProps={{ "aria-label": "Toggle product availability" }}
                  sx={{
                    width: 32,
                    height: 18,
                    p: 0,
                    opacity: 0.88,
                    "& .MuiSwitch-switchBase": {
                      p: "2px",
                      "&.Mui-checked": {
                        transform: "translateX(14px)",
                        color: "#f9f3ef",
                        "& + .MuiSwitch-track": {
                          bgcolor: "rgba(131, 60, 65, 0.88)",
                          opacity: 1,
                        },
                      },
                      "&.Mui-focusVisible .MuiSwitch-thumb": {
                        boxShadow: "0 0 0 3px rgba(131, 60, 65, 0.24)",
                      },
                    },
                    "& .MuiSwitch-thumb": {
                      width: 14,
                      height: 14,
                      boxShadow: "none",
                      bgcolor: "#f0ece8",
                    },
                    "& .MuiSwitch-track": {
                      borderRadius: 18,
                      bgcolor: "#2a1d1b",
                      opacity: 1,
                      border: "1px solid rgba(122, 101, 96, 0.18)",
                    },
                  }}
                />
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default Product;
